import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ApiService } from './api.service';
import { Observable } from 'rxjs';
import { RLMConsumptions, RLMFifteenMinuteConsumptions, RLMHourlyConsumptions, SLPConsumptions } from '@app/shared/models/meter-data/meter-consumption';
import { ViewMeterConsumptionsAggregatedDto, ViewMeterConsumptionsDailyExtendedInfo1Dto } from '@app/shared/models/meter/meters-views';
import { MeterReadingsDailies } from '@app/shared/models/meter/meter-readings-dailies';

@Injectable({
  providedIn: 'root',
})
export class MeterConsumptionService extends ApiService {
  constructor(public http: HttpClient) {
    super(http);
  }

  public getSLPPeriodicConsumption(meterId: string, startDate: string, installationType: string = ''): Observable<SLPConsumptions[]> {
    let url = '';
    url = this.endpoint(`api/meters/slp/meter-id/${meterId}/start-date/${startDate}/consumptions/periodic`);
    if (installationType) {
      url += `?installationType=${installationType}`;
    }
    return this.http.get<SLPConsumptions[]>(url);
  }

  public getSLPYearlyConsumption(meterId: string, startYear: number, endYear: number, installationType: string = ''): Observable<SLPConsumptions[]> {
    let url = '';
    url = this.endpoint(`api/meters/slp/meter-id/${meterId}/start-year/${startYear}/end-year/${endYear}/consumptions/yearly`);
    if (installationType) {
      url += `?installationType=${installationType}`;
    }
    return this.http.get<SLPConsumptions[]>(url);
  }

  public getRLMFifteenMinuteConsumption(
    meterId: string,
    date: string,
    obisId: string,
    isSmd: boolean,
    installationType: string = ''
  ): Observable<RLMFifteenMinuteConsumptions> {
    let url = this.endpoint(`api/meters/rlm/meter-id/${meterId}/date/${date}/consumptions/fifteen-minute?isSmd=${isSmd}`);
    if (obisId) {
      url += `&obisId=${obisId}`;
    }
    if (installationType) {
      url += `&installationType=${installationType}`;
    }
    return this.http.get<RLMFifteenMinuteConsumptions>(url);
  }

  public getRLMHourlyConsumption(
    meterId: string,
    date: string,
    obisId: string,
    isSmd: boolean,
    isStrom: boolean,
    installationType: string = ''
  ): Observable<RLMHourlyConsumptions> {
    let url = this.endpoint(`api/meters/rlm/meter-id/${meterId}/date/${date}/consumptions/hourly?isSmd=${isSmd}&isStrom=${isStrom}`);
    if (obisId) {
      url += `&obisId=${obisId}`;
    }
    if (installationType) {
      url += `&installationType=${installationType}`;
    }
    return this.http.get<RLMHourlyConsumptions>(url);
  }

  public getRLMDailyConsumption(
    meterId: string,
    month: number,
    year: number,
    obisId: string,
    isSmd: boolean,
    isStrom: boolean,
    installationType: string = ''
  ): Observable<RLMConsumptions> {
    let url = this.endpoint(`api/meters/rlm/meter-id/${meterId}/month/${month}/year/${year}/consumptions/daily?isSmd=${isSmd}&isStrom=${isStrom}`);
    if (obisId) {
      url += `&obisId=${obisId}`;
    }
    if (installationType) {
      url += `&installationType=${installationType}`;
    }
    return this.http.get<RLMConsumptions>(url);
  }

  public getRLMWeeklyConsumption(
    meterId: string,
    startDate: string,
    endDate: string,
    obisId: string,
    isSmd: boolean,
    isStrom: boolean,
    installationType: string = ''
  ): Observable<RLMConsumptions> {
    let url = this.endpoint(
      `api/meters/rlm/meter-id/${meterId}/start-date/${startDate}/end-date/${endDate}/consumptions/weekly?isSmd=${isSmd}&isStrom=${isStrom}`
    );
    if (obisId) {
      url += `&obisId=${obisId}`;
    }
    if (installationType) {
      url += `&installationType=${installationType}`;
    }
    return this.http.get<RLMConsumptions>(url);
  }

  public getRLMMonthlyConsumption(
    meterId: string,
    year: number,
    obisId: string,
    isSmd: boolean,
    isStrom: boolean,
    installationType: string = ''
  ): Observable<RLMConsumptions> {
    let url = this.endpoint(`api/meters/rlm/meter-id/${meterId}/year/${year}/consumptions/monthly?isSmd=${isSmd}&isStrom=${isStrom}`);
    if (obisId) {
      url += `&obisId=${obisId}`;
    }
    if (installationType) {
      url += `&installationType=${installationType}`;
    }
    return this.http.get<RLMConsumptions>(url);
  }

  public getRLMYearlyConsumption(
    municipalityKey: string,
    meterId: string,
    startYear: number,
    endYear: number,
    obisId: string,
    isSmd: boolean,
    isStrom: boolean,
    installationType: string = ''
  ): Observable<RLMConsumptions> {
    let url = this.endpoint(
      `api/meters/municipality/${municipalityKey}/rlm/meter-id/${meterId}/start-year/${startYear}/end-year/${endYear}/consumptions/yearly?isSmd=${isSmd}&isStrom=${isStrom}`
    );
    if (obisId) {
      url += `&obisId=${obisId}`;
    }
    if (installationType) {
      url += `&installationType=${installationType}`;
    }
    return this.http.get<RLMConsumptions>(url);
  }

  public getRecentConsumptionDate(meterId: string, isDesc: boolean): Observable<ViewMeterConsumptionsDailyExtendedInfo1Dto[]> {
    let url = this.endpoint(`socrates/meters/api/views`);
    url += `/ViewMeterConsumptionsDailyExtendedInfo1?$filter=(meterid eq ${meterId})`;
    url += `&$orderby=consumptionDate ${isDesc ? 'DESC' : ''}`;
    url += `&$select=consumptionDate`;
    url += `&$top=1`;
    return this.http.get<ViewMeterConsumptionsDailyExtendedInfo1Dto[]>(url);
  }

  public getSMDDailyReading(meterId: string, readingDate: string, municipalityKey: string): Observable<MeterReadingsDailies[]> {
    let url = this.endpoint(`socrates/meters/api/municipalities/${municipalityKey}/meters/${meterId}?$select=guid,meterNumber`);
    url += `&$expand=MeterRegisters($select=obisId;$filter=obisId eq '1-1:1.29.0' or obisId eq '1-1:2.29.0';$expand=MeterReadingsDailies($select=Value00,readingDate;`;
    url += `$filter=readingDate eq ${readingDate};`;
    url += `$top=1))`;
    return this.http.get<MeterReadingsDailies[]>(url);
  }

  public getRecentReadingDate(meterId: string, isDesc: boolean, municipalityKey: string): Observable<MeterReadingsDailies[]> {
    let url = this.endpoint(`socrates/meters/api/municipalities/${municipalityKey}/meters/${meterId}?$select=guid,meterNumber`);
    url += `&$expand=MeterRegisters($select=obisId;$filter=obisId eq '1-1:1.29.0' or obisId eq '1-1:2.29.0';$expand=MeterReadingsDailies($select=Value00,readingDate;`;
    url += `$orderby=readingDate ${isDesc ? 'desc' : 'asc'};$top=1))`;
    return this.http.get<MeterReadingsDailies[]>(url);
  }

  public getSMDConsumptionYears(meterId: string): Observable<ViewMeterConsumptionsAggregatedDto[]> {
    let url = this.endpoint(`socrates/meters/api/views/ViewMeterConsumptionsAggregated?`);
    url += `$filter=(meterId eq ${meterId}`;
    url += ` and yearValue ne 0)`;
    url += `&$select=consumptionYear`;
    url += `&$orderby=consumptionYear`;
    return this.http.get<ViewMeterConsumptionsAggregatedDto[]>(url);
  }
}
